import { useState, useEffect, useCallback, useRef } from 'react';

interface UsePhotoNavigationOptions {
  photosCount: number;
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
  /** 사진 이동 전 콜백 (어노테이션 자동저장 등). true 반환 시 이동 진행 */
  beforeNavigate?: () => Promise<void>;
  /** 줌 상태 확인 (줌 중이면 ESC에서 줌 리셋 먼저) */
  isZoomed?: boolean;
  /** 줌 리셋 함수 */
  resetZoom?: () => void;
  /** 닫기 전 콜백 (저장 확인 등) */
  beforeClose?: () => Promise<void>;
}

export function usePhotoNavigation({
  photosCount,
  currentIndex,
  isOpen,
  onClose,
  onIndexChange,
  beforeNavigate,
  isZoomed = false,
  resetZoom,
  beforeClose,
}: UsePhotoNavigationOptions) {
  const [index, setIndex] = useState(currentIndex);

  // 콜백 refs (deps 안정화)
  const beforeNavigateRef = useRef(beforeNavigate);
  beforeNavigateRef.current = beforeNavigate;
  const beforeCloseRef = useRef(beforeClose);
  beforeCloseRef.current = beforeClose;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onIndexChangeRef = useRef(onIndexChange);
  onIndexChangeRef.current = onIndexChange;
  const resetZoomRef = useRef(resetZoom);
  resetZoomRef.current = resetZoom;

  // index 범위 보정 (사진 삭제 시 배열이 줄어들 수 있음)
  const safeIndex = photosCount > 0 ? Math.min(index, photosCount - 1) : 0;
  useEffect(() => {
    if (safeIndex !== index && photosCount > 0) {
      setIndex(safeIndex);
    }
  }, [safeIndex, index, photosCount]);

  // 외부 인덱스 변경 동기화
  useEffect(() => {
    setIndex(currentIndex);
  }, [currentIndex]);

  // body overflow 관리
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  /**
   * ⚠️ **`before*` 콜백이 거부당해도 이동·닫기는 «진행»한다 — 심층 방어**(2026-10-13).
   *
   * 이 훅은 `await beforeNavigate()` / `await beforeClose()` **뒤에** 상태 전이(`setIndex`·`onClose`)를
   * 한다. 그래서 그 콜백이 reject하면 전이가 **실행되지 않고** 뷰어가 열린 채 굳는다
   * (`body.overflow:hidden`이 남아 새로고침 말고 길이 없다). 실제 경로가 있다: 어노테이션 자동저장이
   * 사진 쓰기 락 400을 받는 레이스(`inspection-photo.service.savePhotoAnnotation`).
   *
   * ⚠️⚠️ **그런데 «오늘의» 어노테이션 경로에서는 이 catch가 발동하지 않는다** —
   * `usePhotoAnnotation`의 `handleSave`가 거부를 스스로 흡수하고 `onError`가 알리기 때문이다.
   * 즉 방어가 두 겹이고, **`[되돌림: 닫기]`·`[되돌림: 이동]`은 둘을 «함께» 되돌려야 red가 된다**
   * (한 겹만 되돌리면 형제가 덮는다). 이 겹 단독의 탐지기는 `[되돌림: 심층 방어]` 하나이고,
   * 그 케이스는 어노테이션을 거치지 않고 **reject하는 `before*`를 직접 넘긴다**
   * (`__tests__/photoAnnotationSaveFailure.test.tsx`의 표가 네 조합을 소유자별로 적는다).
   * 그러니 이 catch를 「실제 경로에서 안 쓰인다」며 지우지 말 것: 되돌릴 수 없는 결과(새로고침 강요)를
   * 막고, `before*`를 **다른 호스트가 제공할 때**(TBM `customApi` 등) 그 호스트의 실수를 흡수한다.
   */
  const changePhoto = useCallback(async (newIndex: number) => {
    if (beforeNavigateRef.current) {
      try {
        await beforeNavigateRef.current();
      } catch {
        // 자동저장 실패가 사진 이동을 막지 않는다(알림은 콜백 제공자의 몫).
      }
    }
    setIndex(newIndex);
    onIndexChangeRef.current?.(newIndex);
  }, []);

  const goToPrevious = useCallback(() => {
    if (index > 0) {
      changePhoto(index - 1);
    }
  }, [index, changePhoto]);

  const goToNext = useCallback(() => {
    if (index < photosCount - 1) {
      changePhoto(index + 1);
    }
  }, [index, photosCount, changePhoto]);

  /** 닫기 — `beforeClose`가 거부당해도 **반드시 닫는다**(위 `changePhoto` JSDoc의 같은 축) */
  const handleClose = useCallback(async () => {
    if (beforeCloseRef.current) {
      try {
        await beforeCloseRef.current();
      } catch {
        // 저장 실패가 닫기를 막지 않는다 — 막으면 빠져나갈 길이 없다.
      }
    }
    onCloseRef.current();
  }, []);

  // 키보드 이벤트
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          if (isZoomed && resetZoomRef.current) {
            resetZoomRef.current();
          } else {
            handleClose();
          }
          break;
        case 'ArrowLeft':
          if (!isZoomed) goToPrevious();
          break;
        case 'ArrowRight':
          if (!isZoomed) goToNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isZoomed, goToPrevious, goToNext, handleClose]);

  return {
    index: safeIndex,
    setIndex,
    goToPrevious,
    goToNext,
    changePhoto,
    handleClose,
    hasPrevious: safeIndex > 0,
    hasNext: safeIndex < photosCount - 1,
  };
}
